/**
 * Performances BtoB mois par mois, sur douze mois, face au même mois un an
 * avant. Un tableau : on y lit des montants précis et on les compare ligne à
 * ligne, ce qu'une courbe rend mal sur des ventes irrégulières.
 */

import type { BtobMonthRow } from '@shared/types/btob';

import { formatInteger, formatMoney, formatMonthShort } from '../lib/format';
import { relativeChange } from '../lib/sales';
import { IMPACT_TONE_COLORS } from '../lib/theme';
import { impactTone, relativeVerdict, verdictGlyph, verdictLabel } from '../lib/verdict';
import { Card } from './Card';

function Change({ current, reference }: { current: number; reference: number }) {
  if (reference === 0) {
    return <td className="num" style={{ color: IMPACT_TONE_COLORS.neutral }}>{current > 0 ? 'nouveau' : '—'}</td>;
  }
  const change = relativeChange(current, reference);
  const verdict = relativeVerdict(change, 5);
  return (
    <td className="num" style={{ color: IMPACT_TONE_COLORS[impactTone(verdict, true)] }}>
      <span aria-hidden="true">{verdictGlyph(verdict)}</span>{' '}
      {change === null ? '—' : `${change >= 0 ? '+' : '−'}${Math.abs(change).toLocaleString('fr-FR')} %`}
      <span className="sr-only"> — {verdictLabel(verdict)}</span>
    </td>
  );
}

export function BtobMonthsTable({ rows }: { rows: readonly BtobMonthRow[] }) {
  const total = rows.reduce(
    (acc, row) => ({
      revenueHt: acc.revenueHt + row.revenueHt,
      units: acc.units + row.units,
      invoices: acc.invoices + row.invoices,
      yearAgoRevenueHt: acc.yearAgoRevenueHt + row.yearAgoRevenueHt,
      yearAgoUnits: acc.yearAgoUnits + row.yearAgoUnits,
    }),
    { revenueHt: 0, units: 0, invoices: 0, yearAgoRevenueHt: 0, yearAgoUnits: 0 },
  );

  return (
    <Card
      title="Performances dans le temps"
      subtitle="Douze mois jusqu'à la fin de la période, comparés au même mois un an avant — CA HT"
      className="chart-grid__wide"
    >
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Mois</th>
              <th className="num">CA HT</th>
              <th className="num">vs an dernier</th>
              <th className="num">Unités</th>
              <th className="num">vs an dernier</th>
              <th className="num">Factures</th>
              <th className="num">Magasins</th>
            </tr>
          </thead>
          <tbody>
            {[...rows].reverse().map((row) => (
              <tr key={row.month}>
                <td>{formatMonthShort(row.month)}</td>
                <td className="num">{formatMoney(row.revenueHt, 'EUR')}</td>
                <Change current={row.revenueHt} reference={row.yearAgoRevenueHt} />
                <td className="num">{formatInteger(row.units)}</td>
                <Change current={row.units} reference={row.yearAgoUnits} />
                <td className="num">{formatInteger(row.invoices)}</td>
                <td className="num">{formatInteger(row.stores)}</td>
              </tr>
            ))}
            <tr>
              <td><strong>12 mois</strong></td>
              <td className="num"><strong>{formatMoney(total.revenueHt, 'EUR')}</strong></td>
              <Change current={total.revenueHt} reference={total.yearAgoRevenueHt} />
              <td className="num"><strong>{formatInteger(total.units)}</strong></td>
              <Change current={total.units} reference={total.yearAgoUnits} />
              <td className="num"><strong>{formatInteger(total.invoices)}</strong></td>
              <td className="num" />
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
}
